import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { ButtonBase, Icon, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  StoreUtil,
  PatientType,
  PatientTypes,
  LocationTypeUtil,
} from 'doctivity-shared/utils';
import { LoadingView } from './LoadingView';

const styles = (theme) => ({
  // Table container
  tableContainer: {
    width: '100%',
    fontSize: 12,
  },
  // Header row
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#EBF1F8',
    borderRadius: '4px 4px 0 0',
    padding: '8px 0',
    marginBottom: 2,
    width: '100%',
  },
  headerCell: {
    color: '#64748B',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: '0 8px',
    whiteSpace: 'nowrap',
  },
  // Data rows
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: 2,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F8FAFC',
    },
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Column widths - desktop
  iconCol: {
    width: '2%',
    minWidth: 28,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: 24,
      minWidth: 24,
      padding: 0,
    },
  },
  nameCol: {
    width: '40%',
    minWidth: 200,
  },
  facilityTypeCol: {
    width: '12%',
    minWidth: 100,
  },
  socCol: {
    width: '5%',
    minWidth: 50,
  },
  patientsCol: {
    width: 100,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  chartCol: {
    flex: 1,
    minWidth: 150,
    paddingRight: 12,
  },
  // Mobile column widths
  iconColMobile: {
    width: 24,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  nameColMobile: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  socColMobile: {
    width: 45,
    flexShrink: 0,
    textAlign: 'center',
  },
  patientsColMobile: {
    width: 55,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,
  },
  marketPctColMobile: {
    width: 50,
    flexShrink: 0,
    textAlign: 'right',
  },
  // Cell content styles
  orgName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  facilityType: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  soc: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  affiliatedIcon: {
    fontSize: 14,
    color: '#5391D5',
  },
  patientIcon: {
    fontSize: 12,
    color: '#9CA3AF',
    marginRight: 4,
    [theme.breakpoints.down('md')]: {
      fontSize: 10,
      marginRight: 2,
    },
  },
  patientCount: {
    fontSize: 11,
    fontWeight: 400,
    color: '#4B5563',
  },
  marketPct: {
    fontSize: 11,
    fontWeight: 300,
    color: '#4B5563',
  },
  patientPct: {
    fontSize: 11,
    fontWeight: 300,
    color: '#4B5563',
    marginLeft: 8,
  },
  // Bar chart
  barContainer: {
    width: '100%',
    height: 18,
    backgroundColor: 'transparent',
    borderRadius: 2,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.5s ease-in-out',
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
});

function OrgProjectedPatientsChart({ classes, patientType, careSetting }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const organizations = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_PER_ORGANIZATION'),
  );
  const organizationCounts = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_PER_ORGANIZATION_COUNTS'),
  );
  const [claimRows, setClaimRows] = useState([]);
  const [maxValue, setMaxValue] = useState(100);
  let isLoading = false;

  // Title case for facility type
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // SOC abbreviation based on location_type - only shown when careSetting is "All" (0 or undefined) and not Captured Claims
  const isCapturedClaims = patientType === PatientType.CAPTURED_CLAIMS;
  const showSOC = !isCapturedClaims && (careSetting === 0 || careSetting === undefined || careSetting === null);
  const getSOCAbbreviation = (locationType) => {
    const locationTypeInfo = LocationTypeUtil.getAll().find(lt => lt.id === locationType);
    if (!locationTypeInfo) return '';
    // Return abbreviation if available, otherwise shorten the name
    if (locationTypeInfo.abbreviation) return locationTypeInfo.abbreviation;
    // Fallback abbreviations for common types
    switch (locationType) {
      case 1: return 'ASC';
      case 2: return 'Outpt';
      case 3: return 'Office';
      case 4: return 'Inpt';
      case 5: return 'Lab';
      case 10: return 'UC';
      default: return locationTypeInfo.name ? locationTypeInfo.name.substring(0, 6) : '';
    }
  };

  useEffect(() => {
    let max = 100;
    let rows = [];
    let totalMarket = 0;
    
    if (!organizations || !StoreUtil.isLoaded(organizations)) {
      isLoading = true;
    } else {
      rows = organizations.data.rows;
      if (organizationCounts?.data && organizationCounts.data.total) {
        totalMarket = organizationCounts.data.total;
      }
    }

    rows.forEach((claims) => {
      if (claims.total === null) {
        claims.total = 0;
      } else {
        claims.total = parseInt(claims.total, 10);
        if (totalMarket) {
          claims.aggregateClaimsPercentage = claims.total / totalMarket;
        }
        if (
          claims.organization.is_affiliated ||
          claims.organization.affiliated?.length > 0
        ) {
          claims.affiliated = true;
        }
        max = Math.max(max, claims.total);
      }
    });
    setClaimRows(rows);
    setMaxValue(max);
  }, [
    StoreUtil.isLoaded(organizations),
    organizations?.data?.rows,
    organizationCounts?.data?.rows,
  ]);

  if (isLoading) return <LoadingView />;

  // Mobile view: Icon, Name, SOC, Patients & %
  if (isMobile) {
    return (
      <div className={classes.tableContainer}>
        {/* Header */}
        <div className={classes.headerRow}>
          <div className={`${classes.headerCell} ${classes.iconColMobile}`}></div>
          <div className={`${classes.headerCell} ${classes.nameColMobile}`}>Organization</div>
          {showSOC && (
            <div className={`${classes.headerCell} ${classes.socColMobile}`}>SOC</div>
          )}
          <div className={`${classes.headerCell} ${classes.patientsColMobile}`} style={{ width: 105, justifyContent: 'flex-end', textAlign: 'right' }}>
            {isCapturedClaims ? 'Claims & %' : 'Patients & %'}
          </div>
        </div>

        {/* Data Rows */}
        {claimRows.map((claims, index) => (
          <ButtonBase
            key={`claims:${claims.org_id}:${claims.location_type}:${index}`}
            className={classes.dataRow}
            href={`/organizations/${claims.organization.id}/market`}
          >
            {/* Icon Column */}
            <div className={`${classes.cell} ${classes.iconColMobile}`}>
              {claims.affiliated && (
                <Tooltip
                  placement='bottom-start'
                  title={
                    <Typography className={classes.infoTip}>
                      Affiliated Organization
                    </Typography>
                  }
                >
                  <Icon className={classes.affiliatedIcon}>hub</Icon>
                </Tooltip>
              )}
            </div>

            {/* Organization Name */}
            <div className={`${classes.cell} ${classes.nameColMobile}`}>
              <span className={classes.orgName}>
                {claims.organization.name}
              </span>
            </div>

            {/* SOC Column */}
            {showSOC && (
              <div className={`${classes.cell} ${classes.socColMobile}`}>
                <span className={classes.soc}>
                  {getSOCAbbreviation(claims.location_type)}
                </span>
              </div>
            )}

            {/* Patients Count & Market % combined */}
            <div className={`${classes.cell} ${classes.patientsColMobile}`} style={{ width: 105 }}>
              {!isCapturedClaims && (
                <Icon className={classes.patientIcon}>person</Icon>
              )}
              <span className={classes.patientCount}>
                {claims.total ? claims.total.toLocaleString() : '<11'}
              </span>
              <span className={classes.marketPct} style={{ marginLeft: 8 }}>
                {claims.aggregateClaimsPercentage !== null && claims.aggregateClaimsPercentage !== undefined
                  ? `${(claims.aggregateClaimsPercentage * 100).toFixed(2)}%`
                  : '-'}
              </span>
            </div>
          </ButtonBase>
        ))}
      </div>
    );
  }

  // Desktop view: Full table
  return (
    <div className={classes.tableContainer}>
      {/* Header */}
      <div className={classes.headerRow}>
        <div className={`${classes.headerCell} ${classes.iconCol}`}></div>
        <div className={`${classes.headerCell} ${classes.nameCol}`}>Organization</div>
        <div className={`${classes.headerCell} ${classes.facilityTypeCol}`}>Facility Type</div>
        {showSOC && (
          <div className={`${classes.headerCell} ${classes.socCol}`}>SOC</div>
        )}
        <div className={`${classes.headerCell} ${classes.patientsCol}`}>
          {isCapturedClaims ? 'Claims & Market %' : 'Patients & Market %'}
        </div>
        <div className={`${classes.headerCell} ${classes.chartCol}`}></div>
      </div>

      {/* Data Rows */}
      {claimRows.map((claims, index) => {
        const facilityType = claims.organization?.facility_type || '';
        const barWidth = claims.total > 0 ? (claims.total / maxValue) * 100 : 0;
        const barColor = claims.affiliated 
          ? '#5391D5'  // Blue for affiliated
          : '#FF6B6B'; // Red for non-affiliated

        return (
          <ButtonBase
            key={`claims:${claims.org_id}:${claims.location_type}:${index}`}
            className={classes.dataRow}
            href={`/organizations/${claims.organization.id}/market`}
          >
            {/* Icon Column */}
            <div className={`${classes.cell} ${classes.iconCol}`}>
              {claims.affiliated && (
                <Tooltip
                  placement='bottom-start'
                  title={
                    <Typography className={classes.infoTip}>
                      Affiliated Organization
                    </Typography>
                  }
                >
                  <Icon className={classes.affiliatedIcon}>hub</Icon>
                </Tooltip>
              )}
            </div>

            {/* Organization Name */}
            <div className={`${classes.cell} ${classes.nameCol}`}>
              <span className={classes.orgName}>
                {claims.organization.name}
              </span>
            </div>

            {/* Facility Type */}
            <div className={`${classes.cell} ${classes.facilityTypeCol}`}>
              <span className={classes.facilityType}>
                {toTitleCase(facilityType) || '-'}
              </span>
            </div>

            {/* SOC Column */}
            {showSOC && (
              <div className={`${classes.cell} ${classes.socCol}`}>
                <span className={classes.soc}>
                  {getSOCAbbreviation(claims.location_type)}
                </span>
              </div>
            )}

            {/* Patients Count & Percentage */}
            <div className={`${classes.cell} ${classes.patientsCol}`}>
              {!isCapturedClaims && (
                <Icon className={classes.patientIcon}>person</Icon>
              )}
              <span className={classes.patientCount}>
                {claims.total ? claims.total.toLocaleString() : '<11'}
              </span>
              {claims.aggregateClaimsPercentage !== null &&
                claims.aggregateClaimsPercentage !== undefined && (
                <span className={classes.patientPct}>
                  {(claims.aggregateClaimsPercentage * 100).toFixed(2)}%
                </span>
              )}
            </div>

            {/* Bar Chart */}
            <div className={`${classes.cell} ${classes.chartCol}`}>
              <div className={classes.barContainer}>
                <div
                  className={classes.bar}
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: barColor,
                  }}
                />
              </div>
            </div>
          </ButtonBase>
        );
      })}
    </div>
  );
}

OrgProjectedPatientsChart.propTypes = {
  classes: PropTypes.object,
  patientType: PropTypes.oneOf(PatientTypes).isRequired,
  careSetting: PropTypes.number,
};

const Styled = withStyles(styles)(OrgProjectedPatientsChart);
export { Styled as OrgProjectedPatientsChart };